import React, { ChangeEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
    BodyText1,
    Button,
    Dropdown,
    FieldLabel,
    FlexColumn,
    FlexRow,
} from '@portal/common/components';
import { styled } from '@portal/common/theme';

import config from '../../../config';
import _ from '../../../locale';

const YearDropdown = styled(Dropdown)({
    width: 184,
});

const YearsDivider = styled(BodyText1)(({ theme }) => ({
    paddingTop: 42,
    color: theme.color.gray40,
}));

const AddBtn = styled(Button)({
    marginTop: 30,
    minWidth: 0,
    width: 68,
});

const sortRange = (value: number[]): number[] => value.sort((a, b) => a - b);

export const NO_BOUND_VALUE = 'null';

interface CustomAgeGroupSelectorProps {
    isSingleRangeReport?: boolean;
    supportedAgeRange: {
        min: number;
        max: number;
    };
    selectedMetrics: number[];
    onChange: (range: (number | string)[]) => void;
    onSave: (range: (number | string)[]) => void;
}

const CustomAgeGroupSelector: FC<CustomAgeGroupSelectorProps> = ({
    isSingleRangeReport,
    supportedAgeRange: { min, max },
    selectedMetrics,
    onChange,
    onSave,
}) => {
    const [startValue, setStartValue] = useState(min);
    const [endValue, setEndValue] = useState(max);

    const areNoBoundsAllowed = useMemo(() => {
        if (isSingleRangeReport) {
            return false;
        }
        const hasNumberMetric = (selectedMetrics || []).includes(config.numberMetricId);
        return hasNumberMetric && !isSingleRangeReport;
    }, [isSingleRangeReport, selectedMetrics]);

    const startYearOptions = useMemo(() => {
        const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);

        // Add 0 option at the beginning
        if (areNoBoundsAllowed && min > 0) {
            options.unshift(0);
        }
        return options;
    }, [min, max, isSingleRangeReport]);

    const endYearOptions = useMemo(() => {
        const options: (number | string)[] = Array.from(
            { length: max - min + 1 },
            (_, i) => min + i
        );
        // Add "No bound" option at the end
        areNoBoundsAllowed && options.push(NO_BOUND_VALUE);
        return options;
    }, [min, max, isSingleRangeReport]);

    const setRangeValues = useCallback((range: number[]) => {
        const [start, end] = sortRange(range);
        setStartValue(start);
        setEndValue(end);
    }, []);

    const handleStartChange = useCallback(
        (ev: ChangeEvent<HTMLSelectElement>) => {
            const value = Number(ev.target.value);
            setRangeValues([value, endValue]);
        },
        [endValue, setRangeValues]
    );

    const handleEndChange = useCallback(
        (ev: ChangeEvent<HTMLSelectElement>) => {
            const value =
                ev.target.value === NO_BOUND_VALUE ? NO_BOUND_VALUE : Number(ev.target.value);
            setRangeValues([startValue, value]);
        },
        [startValue, setRangeValues]
    );

    const customAgeLabelGetter = useCallback(
        (o) => (o === NO_BOUND_VALUE ? `${max + 1}+` : String(o)),
        [max]
    );

    const handleAddClick = useCallback(() => {
        onSave(sortRange([startValue, endValue]));
    }, [startValue, endValue, onSave]);

    useEffect(() => {
        onChange(sortRange([startValue, endValue]));
    }, [startValue, endValue, onChange]);

    useEffect(() => {
        if (isSingleRangeReport) {
            const newStartValue = startValue < min ? min : startValue;
            const newEndValue = endValue === NO_BOUND_VALUE ? max : endValue;
            setRangeValues([newStartValue, newEndValue]);
        }
    }, [isSingleRangeReport]);

    return (
        <>
            <FieldLabel>{_('export_single_age_range_slider_title')}</FieldLabel>
            <FlexRow itemsSpacing={12}>
                <FlexColumn itemsSpacing={12}>
                    <FieldLabel>{_('export_single_report_start_label')}</FieldLabel>
                    <YearDropdown
                        id="age-start"
                        aria-label={_('export_single_report_start_label')}
                        options={startYearOptions}
                        valueMode="single"
                        value={startValue}
                        onChange={handleStartChange}
                        optionDisabledGetter={(o: number) => !isSingleRangeReport && o === endValue}
                    />
                </FlexColumn>
                <YearsDivider>-</YearsDivider>
                <FlexColumn itemsSpacing={12}>
                    <FieldLabel>{_('export_single_report_end_label')}</FieldLabel>
                    <YearDropdown
                        options={endYearOptions}
                        valueMode="single"
                        value={endValue}
                        onChange={handleEndChange}
                        optionDisabledGetter={(o) => !isSingleRangeReport && o === startValue}
                        optionLabelGetter={customAgeLabelGetter}
                    />
                </FlexColumn>
                {!isSingleRangeReport && (
                    <AddBtn onClick={handleAddClick}>
                        {_('export_single_report_add_another_aggregate_btn')}
                    </AddBtn>
                )}
            </FlexRow>
        </>
    );
};

export default memo(CustomAgeGroupSelector);
